import { useLingui } from '@lingui/react/macro';
import { useCallback } from 'react';
import { activateLocale, type SupportedLocale, supportedLocales, toSupportedLocale } from './i18n.ts';

/**
 * Single entry point for reading and changing the UI language, shared by every picker surface
 * (header menu, mobile drawer, preferences drawer) so they can't drift apart.
 *
 * Only the message language is switched here — number and date formats keep following the browser
 * locale, see `activateLocale`.
 */
export function useLocaleSelection() {
  const { i18n } = useLingui();
  const currentLocale = toSupportedLocale(i18n.locale);

  const selectLocale = useCallback(
    (locale: SupportedLocale) => {
      if (locale === i18n.locale) return;
      void activateLocale(locale).then((applied) => {
        // Persist only what actually activated: storing a locale whose catalog failed to
        // load would degrade every future boot, not just this session.
        if (applied === locale) {
          try {
            localStorage.setItem('locale', applied);
          } catch {
            // Storage may be blocked; the choice still applies for this session.
          }
        }
      });
    },
    [i18n.locale]
  );

  return { currentLocale, locales: supportedLocales, selectLocale };
}
